import * as React from 'react';
import { type StyleProp, TouchableOpacity, View, type ViewStyle } from 'react-native';

import { Icon } from '@gitbook/spine-icons';
import { Description, type IconDefinition, Label, renderIcon } from '@gitbook/spine-react';

import { flex, sizes, spacing } from '../constants';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';

export type CheckBoxProps = {
    multi?: boolean;
    size?: number;
    value: boolean;
    onValueChange: (value: boolean) => void;
    readOnly?: boolean;
    disabled?: boolean;

    label?: React.ReactNode;
    labelIcon?: IconDefinition;
    labelDescription?: React.ReactNode;

    style?: StyleProp<ViewStyle>;
};

/**
 * Checkbox is shown as a square box that is ticked (checked) when activated.
 */
export function CheckBox(props: CheckBoxProps): React.ReactElement {
    const { label, labelIcon, labelDescription, value, onValueChange, style } = props;

    const id = React.useId();
    const inputID = `checkbox-input-${id}`;
    const labelID = label ? `checkbox-label-${id}` : undefined;

    const checkbox = <Checkbox {...props} inputID={inputID} labelID={labelID} />;

    if (!label) {
        return checkbox;
    }

    function onValueToggle() {
        onValueChange(!value);
    }

    return (
        <View style={[flex.row, flex.alignItemsCenter, style]}>
            <View style={[flex.row, spacing.Margin.right400]}>{checkbox}</View>
            <View style={[flex.flex1]}>
                <View
                    style={[
                        flex.row,
                        flex.alignItemsCenter,
                        labelDescription ? spacing.Margin.bottom100 : null,
                    ]}
                >
                    {labelIcon ? renderIcon(labelIcon, { className: 'mr-2', size: 18 }) : null}
                    <Label onClick={onValueToggle} htmlFor={inputID} id={labelID}>
                        {label}
                    </Label>
                </View>
                {labelDescription ? (
                    <View>
                        <Description onClick={onValueToggle}>{labelDescription}</Description>
                    </View>
                ) : null}
            </View>
        </View>
    );
}

function Checkbox(
    props: CheckBoxProps & {
        inputID: string;
        labelID: string | undefined;
    }
) {
    const {
        readOnly,
        disabled,
        value,
        onValueChange,
        size = sizes.ICON_400,
        inputID,
        labelID,
        multi = true,
    } = props;

    const [, styles] = useCurrentTheme(themedStyles);

    if (readOnly || disabled) {
        return (
            <View
                style={[
                    styles.checkbox,
                    multi ? null : styles.radio,
                    value && styles.checkboxChecked,
                    disabled && styles.disabled,
                    !value && disabled && styles.checkboxDisabledUnchecked,
                    {
                        width: size,
                        height: size,
                        borderRadius: multi ? undefined : size,
                    },
                ]}
            >
                {value ? <Icon.Check size={size * 0.9} style={styles.icon} /> : null}
            </View>
        );
    }

    return (
        <TouchableOpacity
            style={[
                styles.checkbox,
                styles.checkboxEditable,
                value && styles.checkboxChecked,
                multi ? null : styles.radio,
                {
                    width: size,
                    height: size,
                    borderRadius: multi ? undefined : size,
                },
            ]}
            onPress={() => onValueChange(!value)}
            accessibilityRole="checkbox"
            accessibilityLabelledBy={labelID}
            // @ts-ignore - accessibilityChecked is supported by rnw and exposes the checked state to screen readers
            accessibilityChecked={value}
            nativeID={inputID}
        >
            {value ? (
                // @ts-ignore
                <Icon.Check size={size * 0.7} style={styles.icon} accessibilityHidden />
            ) : null}
        </TouchableOpacity>
    );
}

CheckBox.defaultProps = {
    size: sizes.ICON_400,
};

const themedStyles = ThemedStyleSheet.create({
    icon: () => ({
        color: 'var(--sp-color-text-white)',
        strokeWidth: 3,
    }),
    checkbox: (theme) => ({
        borderRadius: theme.radiuses.medium,
        borderColor: 'var(--sp-color-border-base)',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
    }),
    radio: () => ({
        borderWidth: 2,
    }),
    disabled: () => ({
        opacity: 0.38,
    }),
    checkboxEditable: {
        cursor: 'pointer',
    } as ViewStyle,
    checkboxChecked: () => ({
        backgroundColor: 'var(--sp-color-bg-form-radio-checked-default)',
        borderColor: 'var(--sp-color-border-form-radio-checked-default)',
    }),
    checkboxDisabledUnchecked: () => ({
        backgroundColor: 'var(--sp-color-bg-inactive)',
        borderColor: 'var(--sp-color-border-form-radio-unchecked-disabled)',
    }),
});
